import { create } from 'zustand';
import type { Patient, FilterConfig } from '../types';
import { patientService } from '../services/patient.service';

interface PatientState {
  patients: Patient[];
  selectedPatient: Patient | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: FilterConfig;

  // Actions
  fetchPatients: (page?: number, filters?: FilterConfig) => Promise<void>;
  getPatientById: (id: string) => Promise<Patient>;
  setSelectedPatient: (patient: Patient | null) => void;
  setFilters: (filters: Partial<FilterConfig>) => void;
  setPage: (page: number) => void;
  clearError: () => void;
}

export const usePatientStore = create<PatientState>((set, get) => ({
  patients: [],
  selectedPatient: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  filters: { search: '' },

  fetchPatients: async (page?: number, filters?: FilterConfig) => {
    const { currentPage, pageSize, filters: currentFilters } = get();
    const pageToFetch = page ?? currentPage;
    const filtersToUse = filters ?? currentFilters;

    set({ isLoading: true, error: null });
    try {
      const response = await patientService.getPatients(pageToFetch, pageSize, filtersToUse);
      set({
        patients: response.data,
        totalCount: response.total,
        currentPage: response.page,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch patients';
      set({ error: message, isLoading: false });
    }
  },

  getPatientById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const patient = await patientService.getPatientById(id);
      set({ selectedPatient: patient, isLoading: false });
      return patient;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch patient';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedPatient: (patient: Patient | null) => set({ selectedPatient: patient }),

  setFilters: (filters: Partial<FilterConfig>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      currentPage: 1,
    }));
  },

  setPage: (page: number) => set({ currentPage: page }),

  clearError: () => set({ error: null }),
}));



